/**
 * @file Functions used when importing from JSON and exporting to JSON
 */

import { Core, EdgeSingular, NodeSingular } from 'cytoscape';
import { ACTIONS } from '../components/App';
import { DiagramTreeNode, diagramTreeRoot } from '../model/diagram-tree-model';
import {
  derivedEdgeArray,
  EdgeArray,
  EdgeType,
  hierarchicalStructuralEdges,
  LinkNegation,
  LinkQualifier,
  MMEdge,
  originalEdgeArray,
} from '../model/edge-model';
import {
  masterModelRoot,
  MMNode,
  MMRoot,
  NodeType,
  ChildGroupType,
} from '../model/node-model';
import { eleCounter } from './elementCounter';
import { applyAutoLabelWidth } from '../utils/node-label-size';
import { buildMarkerLabel } from '../options/link-marker-config';
import { recalculateNamespacesForMasterModel } from '../utils/node-namespace';

/**
 * 计算节点的显示标签
 * 如果节点是值分组类型（包含值子节点），则显示时添加 [] 后缀
 * @param label - 原始标签
 * @param mmRef - 节点自身的 MMRef（用于检查是否为值分组）
 * @returns 显示标签
 */
export const computeDisplayLabel = (
  label: string,
  mmRef?: MMNode | null
): string => {
  if (mmRef && mmRef.childGroupType === ChildGroupType.Value) {
    return `${label} []`;
  }
  return label;
};

//random layout options
let options = {
  name: 'random',
  fit: false,
  padding: 30,
  boundingBox: {},
  avoidOverlap: true,
};

let currentMMNode = masterModelRoot;

/**
 * 收集 master model 中仍有效（未删除）的全部节点。
 */
const collectActiveMMNodes = (): MMNode[] => {
  const result: MMNode[] = [];
  const walk = (node: MMNode) => {
    if (!node || node.deleted) return;
    result.push(node);
    (node.children || []).forEach(child => walk(child));
  };
  (masterModelRoot.children || []).forEach(child => walk(child));
  return result;
};

const detachDiagramSubtree = (diagram: DiagramTreeNode | null | undefined) => {
  if (!diagram || diagram === diagramTreeRoot) {
    return;
  }

  const walk = (currentDiagram: DiagramTreeNode) => {
    if (
      currentDiagram?.mainNode instanceof MMNode &&
      currentDiagram.mainNode.diagram === currentDiagram
    ) {
      currentDiagram.mainNode.diagram = null;
    }
    (currentDiagram.children || []).forEach(childDiagram => {
      walk(childDiagram);
    });
  };

  walk(diagram);
  diagram.parent?.removeChild(diagram);
  diagram.parent = null;
};
/**
 * 按规则拆分名称末尾数字：Object12 => { base: Object, nextIndex: 13 }。
 */
const splitTrailingNumber = (value: string) => {
  const normalized = (value || '').trim();
  const matched = normalized.match(/^(.*?)(\d+)$/);
  if (!matched) {
    return {
      base: normalized,
      nextIndex: 1,
    };
  }

  const base = (matched[1] || '').trim();
  const index = Number(matched[2]);
  return {
    base,
    nextIndex: Number.isFinite(index) ? index + 1 : 1,
  };
};

/**
 * 生成全局唯一的“名称+数字后缀”值。
 */
const generateUniqueSequencedValue = (
  rawValue: string,
  usedValues: Set<string>,
  fallbackPrefix = 'Node'
): string => {
  const { base, nextIndex } = splitTrailingNumber(rawValue);
  const normalizedBase =
    (base || rawValue || fallbackPrefix).trim() || fallbackPrefix;

  let index = Math.max(1, nextIndex);
  let candidate = `${normalizedBase}${index}`;
  while (usedValues.has(candidate)) {
    index += 1;
    candidate = `${normalizedBase}${index}`;
  }
  return candidate;
};

const buildUsedNodeLabelSet = (): Set<string> => {
  const set = new Set<string>();
  collectActiveMMNodes().forEach(node => {
    const value = (node.label || '').trim();
    if (value) set.add(value);
  });
  return set;
};

const buildUsedNodeCodeNameSet = (): Set<string> => {
  const set = new Set<string>();
  collectActiveMMNodes().forEach(node => {
    const value = (node.codeName || '').trim();
    if (value) set.add(value);
  });
  return set;
};

const generateUniqueCodeNameByLabel = (
  uniqueLabel: string,
  usedCodeNames: Set<string>
): string => {
  const normalizedLabel = (uniqueLabel || '').trim() || 'Node';
  const baseCodeName = `${normalizedLabel}en`;
  if (!usedCodeNames.has(baseCodeName)) {
    return baseCodeName;
  }
  return generateUniqueSequencedValue(
    baseCodeName,
    usedCodeNames,
    baseCodeName
  );
};

/**
 * 收集父节点范围内已使用的 state/value 名称（仅统计未删除子节点）。
 */
const buildUsedStateValueLabelSetByParent = (
  parentMMRef: MMNode
): Set<string> => {
  const set = new Set<string>();
  (parentMMRef?.children || []).forEach(child => {
    if (child.deleted) return;
    if (child.type !== NodeType.State && child.type !== NodeType.Value) return;
    const value = (child.label || '').trim();
    if (value) set.add(value);
  });
  return set;
};

/**
 * 从 cytoscape/diagramJson 的 data 中提取节点 ID（兼容 id/MMRefId/MMRef.id）。
 */
const getNodeDataId = (data: any): string => {
  const rawId = data?.id ?? data?.MMRefId ?? data?.MMRef?.id;
  if (rawId === undefined || rawId === null) return '';
  return String(rawId);
};

/**
 * 遍历图层树。
 */
const walkDiagramTree = (
  diagram: DiagramTreeNode,
  visitor: (item: DiagramTreeNode) => void
) => {
  if (!diagram) return;
  visitor(diagram);
  (diagram.children || []).forEach(child => walkDiagramTree(child, visitor));
};

/**
 * 同步父节点在图层快照中的分组类型与显示标签。
 */
const syncParentSnapshotMeta = (
  parentSnapshotNode: any,
  parentMMRef: MMNode
) => {
  if (!parentSnapshotNode || !parentMMRef) return;
  const parentData = parentSnapshotNode.data || {};
  const rawParentLabel = (parentMMRef.label || parentData.label || '').trim();
  parentSnapshotNode.data = {
    ...parentData,
    label: rawParentLabel,
    childGroupType: parentMMRef.childGroupType || ChildGroupType.None,
    displayLabel: computeDisplayLabel(rawParentLabel, parentMMRef),
  };
};

const STRUCTURE_CHILD_SELECTOR = '[type="state"], [type="value"]';
const STRUCTURE_CHILD_SIDE_PADDING = 18;
const STRUCTURE_CHILD_TOP_PADDING = 48;
const STRUCTURE_CHILD_BOTTOM_PADDING = 18;
const STRUCTURE_CHILD_HORIZONTAL_GAP = 14;
const STRUCTURE_CHILD_VERTICAL_GAP = 12;
const STRUCTURE_CHILD_PREFERRED_MAX_COLUMNS = 3;
const INZOOM_CHILD_SIDE_PADDING = 12;
const INZOOM_CHILD_TOP_PADDING = 56;
const INZOOM_CHILD_BOTTOM_PADDING = 12;

interface StructureChildLayoutItem {
  node: NodeSingular;
  width: number;
  height: number;
}

interface StructureChildPlacement {
  node: NodeSingular;
  x: number;
  y: number;
}

const toFiniteNumber = (value: unknown): number | null => {
  if (value === undefined || value === null || value === '') {
    return null;
  }
  const parsed = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const getStructureChildrenInOrder = (
  parentNode: NodeSingular,
  excludedNodeId?: string
): NodeSingular[] => {
  const parentMMRef = parentNode.data('MMRef') as MMNode | null;
  const childOrderMap = new Map<string, number>();

  (parentMMRef?.children || []).forEach((child, index) => {
    if (!child || child.deleted) return;
    if (child.type !== NodeType.State && child.type !== NodeType.Value) return;
    childOrderMap.set(String(child.id), index);
  });

  return parentNode
    .children(STRUCTURE_CHILD_SELECTOR)
    .toArray()
    .filter(
      (child: NodeSingular) =>
        child.data('display') !== 'none' && child.id() !== excludedNodeId
    )
    .sort((a, b) => {
      const orderA = childOrderMap.get(String(a.id()));
      const orderB = childOrderMap.get(String(b.id()));

      if (orderA !== undefined && orderB !== undefined && orderA !== orderB) {
        return orderA - orderB;
      }
      if (orderA !== undefined) return -1;
      if (orderB !== undefined) return 1;
      return String(a.id()).localeCompare(String(b.id()), undefined, {
        numeric: true,
        sensitivity: 'base',
      });
    });
};

const getPreferredStructureInnerWidth = (
  items: StructureChildLayoutItem[]
): number => {
  const preferredColumns = Math.min(
    items.length,
    items.length <= 2 ? items.length : STRUCTURE_CHILD_PREFERRED_MAX_COLUMNS
  );

  if (preferredColumns <= 0) {
    return 0;
  }

  return items.slice(0, preferredColumns).reduce((total, item, index) => {
    const gap = index > 0 ? STRUCTURE_CHILD_HORIZONTAL_GAP : 0;
    return total + gap + item.width;
  }, 0);
};

const computeStructureChildPlacements = (
  items: StructureChildLayoutItem[],
  innerWidth: number
): {
  placements: StructureChildPlacement[];
  requiredWidth: number;
  requiredHeight: number;
} => {
  let cursorX = 0;
  let cursorY = 0;
  let currentRowHeight = 0;
  let maxRowWidth = 0;
  const placements: StructureChildPlacement[] = [];

  items.forEach(item => {
    const shouldWrap = cursorX > 0 && cursorX + item.width > innerWidth;
    if (shouldWrap) {
      maxRowWidth = Math.max(
        maxRowWidth,
        cursorX - STRUCTURE_CHILD_HORIZONTAL_GAP
      );
      cursorX = 0;
      cursorY += currentRowHeight + STRUCTURE_CHILD_VERTICAL_GAP;
      currentRowHeight = 0;
    }

    placements.push({
      node: item.node,
      x: cursorX + item.width / 2,
      y: cursorY + item.height / 2,
    });

    cursorX += item.width + STRUCTURE_CHILD_HORIZONTAL_GAP;
    currentRowHeight = Math.max(currentRowHeight, item.height);
  });

  const lastRowWidth =
    cursorX > 0 ? cursorX - STRUCTURE_CHILD_HORIZONTAL_GAP : 0;
  return {
    placements,
    requiredWidth: Math.max(maxRowWidth, lastRowWidth),
    requiredHeight: cursorY + currentRowHeight,
  };
};

const relayoutStateValueChildren = (
  parentNode: NodeSingular,
  excludedNodeId?: string
) => {
  if (!parentNode || parentNode.empty()) return;

  const children = getStructureChildrenInOrder(parentNode, excludedNodeId);
  if (!children.length) return;

  const cy = parentNode.cy();
  const childItems = children.map(node => {
    applyAutoLabelWidth(
      node,
      node.data('label') || node.data('MMRef')?.label || ''
    );

    const width =
      toFiniteNumber(node.data('customWidth')) ??
      Math.max(node.width(), toFiniteNumber(node.data('labelWidth')) ?? 0, 80);
    const height =
      toFiniteNumber(node.data('customHeight')) ?? Math.max(node.height(), 60);

    return {
      node,
      width: Math.ceil(width),
      height: Math.ceil(height),
    };
  });

  const currentWidth = Math.ceil(
    Math.max(
      toFiniteNumber(parentNode.data('customWidth')) ?? 0,
      parentNode.width(),
      120
    )
  );
  const currentHeight = Math.ceil(
    Math.max(
      toFiniteNumber(parentNode.data('customHeight')) ?? 0,
      parentNode.height(),
      150
    )
  );

  const preferredInnerWidth = getPreferredStructureInnerWidth(childItems);
  const innerWidth = Math.max(
    currentWidth - STRUCTURE_CHILD_SIDE_PADDING * 2,
    preferredInnerWidth
  );
  const { placements, requiredWidth, requiredHeight } =
    computeStructureChildPlacements(childItems, innerWidth);
  const targetWidth = Math.ceil(
    Math.max(currentWidth, requiredWidth + STRUCTURE_CHILD_SIDE_PADDING * 2)
  );
  const targetHeight = Math.ceil(
    Math.max(
      currentHeight,
      requiredHeight +
        STRUCTURE_CHILD_TOP_PADDING +
        STRUCTURE_CHILD_BOTTOM_PADDING
    )
  );

  cy.startBatch();

  if (targetWidth !== currentWidth || targetHeight !== currentHeight) {
    parentNode.data({
      customWidth: targetWidth,
      customHeight: targetHeight,
    });
    parentNode.style({
      'min-width': targetWidth,
      'min-height': targetHeight,
    });
  }

  const anchorX =
    parentNode.position('x') - targetWidth / 2 + STRUCTURE_CHILD_SIDE_PADDING;
  const anchorY =
    parentNode.position('y') - targetHeight / 2 + STRUCTURE_CHILD_TOP_PADDING;

  placements.forEach(item => {
    item.node.position({
      x: anchorX + item.x,
      y: anchorY + item.y,
    });
  });

  cy.endBatch();
};

/**
 * 将单个子节点限制在父节点标题安全区之外。
 * 返回 true 表示位置被修正，false 表示无需处理或不适用。
 */
const clampChildNodeWithinParentTitleSafeArea = (
  childNode: NodeSingular,
  parentNode?: NodeSingular
): boolean => {
  if (!childNode || childNode.empty()) return false;

  const resolvedParentNode =
    parentNode && !parentNode.empty() ? parentNode : childNode.parent().first();
  if (!resolvedParentNode || resolvedParentNode.empty()) return false;
  if (resolvedParentNode.data('display') === 'none') return false;

  const parentGroupType = resolvedParentNode.data(
    'childGroupType'
  ) as ChildGroupType;
  const childType = childNode.data('type') as NodeType | string;

  let sidePadding = 0;
  let topPadding = 0;
  let bottomPadding = 0;

  if (
    parentGroupType === ChildGroupType.State ||
    parentGroupType === ChildGroupType.Value
  ) {
    if (childType !== NodeType.State && childType !== NodeType.Value) {
      return false;
    }
    sidePadding = STRUCTURE_CHILD_SIDE_PADDING;
    topPadding = STRUCTURE_CHILD_TOP_PADDING;
    bottomPadding = STRUCTURE_CHILD_BOTTOM_PADDING;
  } else if (parentGroupType === ChildGroupType.InZoom) {
    if (childType !== NodeType.Object && childType !== NodeType.Process) {
      return false;
    }
    sidePadding = INZOOM_CHILD_SIDE_PADDING;
    topPadding = INZOOM_CHILD_TOP_PADDING;
    bottomPadding = INZOOM_CHILD_BOTTOM_PADDING;
  } else {
    return false;
  }

  const parentBB = resolvedParentNode.boundingBox({ includeLabels: false });
  const nodeWidth = childNode.outerWidth();
  const nodeHeight = childNode.outerHeight();
  const halfWidth = Math.max(nodeWidth / 2, 1);
  const halfHeight = Math.max(nodeHeight / 2, 1);
  const minX = parentBB.x1 + sidePadding + halfWidth;
  const maxX = parentBB.x2 - sidePadding - halfWidth;
  const minY = parentBB.y1 + topPadding + halfHeight;
  const maxY = parentBB.y2 - bottomPadding - halfHeight;
  const parentCenterX = (parentBB.x1 + parentBB.x2) / 2;
  const parentCenterY = (parentBB.y1 + parentBB.y2) / 2;
  const currentPosition = childNode.position();
  const nextPosition = {
    x:
      minX <= maxX
        ? Math.min(Math.max(currentPosition.x, minX), maxX)
        : parentCenterX,
    y:
      minY <= maxY
        ? Math.min(Math.max(currentPosition.y, minY), maxY)
        : parentCenterY,
  };

  if (
    Math.abs(nextPosition.x - currentPosition.x) < 0.5 &&
    Math.abs(nextPosition.y - currentPosition.y) < 0.5
  ) {
    return false;
  }

  childNode.position(nextPosition);
  return true;
};

/**
 * 统一修正某个 compound 父节点下的可见子节点位置，确保不会覆盖父节点标题。
 */
const clampCompoundChildrenAwayFromTitle = (parentNode: NodeSingular) => {
  if (!parentNode || parentNode.empty()) return;

  parentNode
    .children()
    .toArray()
    .filter((child: NodeSingular) => child.data('display') !== 'none')
    .forEach((child: NodeSingular) => {
      clampChildNodeWithinParentTitleSafeArea(child, parentNode);
    });
};

const syncStateValueLayoutToDiagrams = (
  parentNode: NodeSingular,
  parentMMRef: MMNode,
  currentDiagram: DiagramTreeNode,
  excludedNodeId?: string
) => {
  if (!parentNode || !parentMMRef || !currentDiagram) return;

  const orderedChildren = getStructureChildrenInOrder(
    parentNode,
    excludedNodeId
  );
  if (!orderedChildren.length) return;

  const parentPosition = parentNode.position();
  const childOffsetMap = new Map<string, { x: number; y: number }>();

  orderedChildren.forEach(child => {
    childOffsetMap.set(String(child.id()), {
      x: child.position('x') - parentPosition.x,
      y: child.position('y') - parentPosition.y,
    });
  });

  walkDiagramTree(diagramTreeRoot, diagram => {
    if (diagram === currentDiagram) return;

    const nodes = diagram.diagramJson?.elements?.nodes;
    if (!Array.isArray(nodes)) return;

    const parentSnapshotNode = nodes.find(
      item => getNodeDataId(item?.data) === String(parentMMRef.id)
    );
    if (!parentSnapshotNode) return;

    syncParentSnapshotMeta(parentSnapshotNode, parentMMRef);

    const snapshotParentPosition = parentSnapshotNode.position || {
      x: 0,
      y: 0,
    };
    nodes.forEach(item => {
      const childId = getNodeDataId(item?.data);
      const offset = childOffsetMap.get(childId);
      if (!offset) return;
      item.position = {
        x: snapshotParentPosition.x + offset.x,
        y: snapshotParentPosition.y + offset.y,
      };
    });
  });
};

/**
 * 当 state/value 子节点新增后，将其同步到所有包含同父节点的其他图层快照。
 */
const syncStateValueNodeAdditionToDiagrams = (
  createdNode: NodeSingular,
  parentMMRef: MMNode,
  currentDiagram: DiagramTreeNode
) => {
  if (!createdNode || !parentMMRef) return;

  const childMMRef = createdNode.data('MMRef') as MMNode | null;
  if (!childMMRef) return;

  const childId = String(childMMRef.id || createdNode.id());
  const parentId = String(parentMMRef.id);
  const childLabel = (
    childMMRef.label ||
    createdNode.data('label') ||
    ''
  ).trim();
  const childDisplayLabel = computeDisplayLabel(childLabel, childMMRef);

  walkDiagramTree(diagramTreeRoot, diagram => {
    if (diagram === currentDiagram) return;

    const elements = diagram.diagramJson?.elements;
    const nodes = elements?.nodes;
    if (!Array.isArray(nodes)) return;

    const parentSnapshotNode = nodes.find(
      item => getNodeDataId(item?.data) === parentId
    );
    if (!parentSnapshotNode) return;

    const alreadyExists = nodes.some(
      item => getNodeDataId(item?.data) === childId
    );
    if (!alreadyExists) {
      const parentPosition =
        parentSnapshotNode.position || createdNode.position();
      nodes.push({
        group: 'nodes',
        data: {
          id: childId,
          label: childLabel,
          displayLabel: childDisplayLabel,
          type: childMMRef.type,
          parent: parentId,
          MMRef: childMMRef,
          codeName: childMMRef.codeName || '',
          childGroupType: childMMRef.childGroupType || ChildGroupType.None,
          display: 'element',
        },
        position: {
          x: parentPosition?.x || 0,
          y: parentPosition?.y || 0,
        },
        removed: false,
        selected: false,
        selectable: true,
        locked: false,
        grabbable: true,
        pannable: false,
        classes: '',
      });
    }

    syncParentSnapshotMeta(parentSnapshotNode, parentMMRef);
  });

  const currentParentNode = createdNode.parent();
  if (currentParentNode.length > 0) {
    syncStateValueLayoutToDiagrams(
      currentParentNode.first() as NodeSingular,
      parentMMRef,
      currentDiagram
    );
  }
};

/**
 * 当 state/value 子节点删除后，将其从所有包含同父节点的图层快照中同步移除。
 */
const syncStateValueNodeRemovalFromDiagrams = (
  removedNodeId: string,
  parentMMRef: MMNode
) => {
  if (!removedNodeId || !parentMMRef) return;

  const removedId = String(removedNodeId);
  const parentId = String(parentMMRef.id);

  walkDiagramTree(diagramTreeRoot, diagram => {
    const elements = diagram.diagramJson?.elements;
    const nodes = elements?.nodes;
    if (!Array.isArray(nodes)) return;

    const parentSnapshotNode = nodes.find(
      item => getNodeDataId(item?.data) === parentId
    );
    if (!parentSnapshotNode) return;

    elements.nodes = nodes.filter(
      item => getNodeDataId(item?.data) !== removedId
    );

    const edges = elements?.edges;
    if (Array.isArray(edges)) {
      elements.edges = edges.filter(edge => {
        const sourceId = String(edge?.data?.source || '');
        const targetId = String(edge?.data?.target || '');
        return sourceId !== removedId && targetId !== removedId;
      });
    }

    syncParentSnapshotMeta(parentSnapshotNode, parentMMRef);
  });
};

/**
 * 获取当前图层空白区域新建“顶层节点”时应挂载到的 master model 父节点。
 * 说明：
 * - 根图层顶层节点仍然挂在 masterModelRoot 下；
 * - 子图里视觉上未被主节点包裹的顶层节点，也应挂在 masterModelRoot 下，
 *   不能错误挂到当前子图主节点下面，否则保存后会被回显成“被主节点包裹”。
 */
const resolveDiagramTopLevelParent = (
  mainNode: MMNode | MMRoot | null | undefined
): MMNode | MMRoot => {
  if (!mainNode || mainNode instanceof MMRoot) {
    return mainNode || masterModelRoot;
  }
  return masterModelRoot;
};

interface NodeData {
  id: string;
  label: string;
  type: NodeType;
  MMRef: MMNode | null;
  parent: string;
}

interface EdgeData {
  id: string;
  label: string;
  type: EdgeType;
  MMRef: MMEdge | null;
  source: MMNode;
  target: MMNode;
  linkQualifier?: LinkQualifier;
  linkNegation?: LinkNegation;
  markerColor?: string;
}

const isOpmcvEdgeDebugEnabled = () =>
  typeof window !== 'undefined' && !!(window as any).__OPMCV_EDGE_DEBUG;

const isParentChildInvocationEdgeData = (data: EdgeData) => {
  if (data.type !== EdgeType.Invocation) return false;
  if (!data.source || !data.target) return false;
  return (
    data.source.parent === data.target || data.target.parent === data.source
  );
};

const getCyParentId = (node: any) => {
  if (!node) return null;
  const parent = node.parent?.();
  if (!parent || parent.length === 0) return null;
  return typeof parent.id === 'function'
    ? parent.id()
    : parent[0]?.id?.() || null;
};

const buildEdgeDebugSnapshot = (edge: any) => {
  const source = edge?.source?.();
  const target = edge?.target?.();
  const bbox = edge?.boundingBox?.() ?? null;
  const renderedBoundingBox = edge?.renderedBoundingBox?.() ?? null;
  return {
    id: edge?.id?.(),
    type: edge?.data?.('type') ?? null,
    label: edge?.data?.('label') ?? '',
    display: edge?.data?.('display') ?? null,
    visible: edge?.visible?.() ?? null,
    sourceId: source?.id?.() ?? null,
    targetId: target?.id?.() ?? null,
    sourceIsChild: source?.isChild?.() ?? false,
    targetIsChild: target?.isChild?.() ?? false,
    sourceParentId: getCyParentId(source),
    targetParentId: getCyParentId(target),
    sourcePosition: source?.position?.() ?? null,
    targetPosition: target?.position?.() ?? null,
    sourceRenderedPosition: source?.renderedPosition?.() ?? null,
    targetRenderedPosition: target?.renderedPosition?.() ?? null,
    bbox,
    bboxSize:
      bbox === null
        ? null
        : {
            w: bbox.w,
            h: bbox.h,
          },
    renderedBoundingBox,
    renderedBBoxSize:
      renderedBoundingBox === null
        ? null
        : {
            w: renderedBoundingBox.w,
            h: renderedBoundingBox.h,
          },
    zIndex: edge?.style?.('z-index') ?? null,
    zCompoundDepth: edge?.style?.('z-compound-depth') ?? null,
    curveStyle: edge?.style?.('curve-style') ?? null,
    edgeDistances: edge?.style?.('edge-distances') ?? null,
    sourceEndpoint: edge?.style?.('source-endpoint') ?? null,
    targetEndpoint: edge?.style?.('target-endpoint') ?? null,
    taxiDirection: edge?.style?.('taxi-direction') ?? null,
    taxiTurn: edge?.style?.('taxi-turn') ?? null,
    taxiTurnMinDistance: edge?.style?.('taxi-turn-min-distance') ?? null,
    segmentWeights: edge?.style?.('segment-weights') ?? null,
    segmentDistances: edge?.style?.('segment-distances') ?? null,
    width: edge?.style?.('width') ?? null,
  };
};

const opmcvEdgeDebugLog = (stage: string, payload: any) => {
  if (!isOpmcvEdgeDebugEnabled()) return;
  console.log('[OPMCV EDGE DEBUG]', stage, JSON.stringify(payload));
};
interface CyViewport {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  w: number;
  h: number;
}

/**
 * Crop viewport by third of the width and height
 * @param viewport - Full viewport
 * @returns - Cropped viewport
 */
const cropViewport = (viewport: CyViewport): CyViewport => {
  const widthDiff = viewport.w / 6;
  const heightDiff = viewport.h / 6;
  viewport.x1 = viewport.x1 + widthDiff;
  viewport.x2 = viewport.x2 - widthDiff;
  viewport.y1 = viewport.y1 + heightDiff;
  viewport.y2 = viewport.y2 - heightDiff;
  viewport.w = viewport.w - widthDiff * 2;
  viewport.h = viewport.h - heightDiff * 2;
  return viewport;
};

// 通用视图自适应
const MAX_FIT_NODES = 38;
// 视图自适应时跳过没有有效坐标的节点，避免 fit 结果异常放大/缩小
const hasFinitePosition = (node: NodeSingular): boolean => {
  const pos = node.position();
  return Number.isFinite(pos.x) && Number.isFinite(pos.y);
};
export const smartFitView = (cy: Core, padding: number = 80) => {
  const visibleNodes = cy
    .nodes()
    .filter(
      node =>
        node.data('display') !== 'none' &&
        !node.data('isMarkerLabel') &&
        hasFinitePosition(node)
    );
  console.log(
    '[OPMCV-fit] start fit: visibleNodes=',
    visibleNodes.length,
    'padding=',
    padding
  );

  if (visibleNodes.length === 0) {
    console.log('[OPMCV-fit] no visible nodes, reset viewport');
    cy.reset();
    return;
  }

  let fitElements;
  if (visibleNodes.length > MAX_FIT_NODES) {
    const centroid = visibleNodes.reduce(
      (acc, node) => {
        const pos = (node as NodeSingular).position();
        return { x: acc.x + pos.x, y: acc.y + pos.y };
      },
      { x: 0, y: 0 }
    );
    centroid.x /= visibleNodes.length;
    centroid.y /= visibleNodes.length;

    const closest = visibleNodes
      .sort((a, b) => {
        const pa = (a as NodeSingular).position();
        const pb = (b as NodeSingular).position();
        const da =
          (pa.x - centroid.x) * (pa.x - centroid.x) +
          (pa.y - centroid.y) * (pa.y - centroid.y);
        const db =
          (pb.x - centroid.x) * (pb.x - centroid.x) +
          (pb.y - centroid.y) * (pb.y - centroid.y);
        return da - db;
      })
      .slice(0, MAX_FIT_NODES);

    const closestIdSet = new Set(closest.map(n => n.id()));
    const edges = closest.connectedEdges().filter(edge => {
      const s = edge.data('source');
      const t = edge.data('target');
      return (
        edge.data('display') !== 'none' &&
        closestIdSet.has(s) &&
        closestIdSet.has(t)
      );
    });
    fitElements = closest.union(edges);
    console.log(
      '[OPMCV-fit] trim to closest nodes:',
      closest.length,
      'edges:',
      edges.length
    );
  } else {
    fitElements = cy
      .elements()
      .filter(ele => ele.data('display') !== 'none')
      .filter(ele =>
        ele.isNode()
          ? hasFinitePosition(ele as NodeSingular) && !ele.data('isMarkerLabel')
          : true
      );
    console.log('[OPMCV-fit] use all visible elements:', fitElements.length);
  }

  if (fitElements.length === 0) {
    console.log('[OPMCV-fit] no fit elements after filtering, reset viewport');
    cy.reset();
    return;
  }

  cy.fit(fitElements, padding);

  // 限制缩放上下界，避免视图过度放大或缩小导致节点“消失”
  const maxZoom = 1.5;
  const minZoom = 0.2;
  const currentZoom = cy.zoom();
  if (currentZoom > maxZoom) {
    cy.zoom(maxZoom);
    cy.center(fitElements);
    console.log('[OPMCV-fit] capped zoom to', maxZoom);
  } else if (currentZoom < minZoom) {
    cy.zoom(minZoom);
    cy.center(fitElements);
    console.log('[OPMCV-fit] raised zoom to min', minZoom);
  }

  console.log('[OPMCV-fit] done, zoom=', cy.zoom());
};

/**
 * Adding node to master model as well as in the current diagram
 * @param cy - Cytoscape instance
 * @param data - Node data
 * @param position - Position to put the node at
 * @param parentMMNode - Master model parent
 */
const addNode = (
  cy: Core,
  data: NodeData,
  position = { x: 0, y: 0 },
  parentMMNode: MMNode | MMRoot
) => {
  if (cy.getElementById(data.id).length > 0) {
    return;
  }
  data['label'] =
    data['label'].charAt(0).toUpperCase() +
    data['label'].substring(1).toLowerCase();
  if (data['MMRef'] === null) {
    let modelNode = new MMNode(data['id'], data['type'], data['label']);
    parentMMNode.addChild(modelNode);
    data['MMRef'] = modelNode;
    if (
      data['parent'] !== '' &&
      data['type'] !== 'state' &&
      data['type'] !== 'value'
    )
      modelNode.isSubelementOfMain = true;
    else if (data['type'] === 'state' || data['type'] === 'value')
      modelNode.isStructurePart = true;
  }

  recalculateNamespacesForMasterModel();

  // 计算显示标签
  // 注意：[] 后缀只显示在值分组的父节点上，不显示在子节点上
  // 子节点（state/value）使用普通标签
  const nodeMMRef = data['MMRef'];
  const displayLabel = computeDisplayLabel(data['label'], nodeMMRef);
  (data as any)['displayLabel'] = displayLabel;

  // 同步 childGroupType 到 cytoscape 节点数据（用于菜单显隐判断）
  const childGroupType = nodeMMRef?.childGroupType || ChildGroupType.None;

  const parentBB = cy
    .getElementById(data.parent)
    .boundingBox({ includeNodes: true });
  cy.elements().lock();

  cy.add({
    data: {
      ...data,
      displayLabel,
      childGroupType,
      namespace: nodeMMRef?.namespace || '',
      packageName: nodeMMRef?.packageName || '',
    },
    position: position,
  });
  const addedNode = cy.getElementById(data.id);
  applyAutoLabelWidth(addedNode, data.label);

  //if no position was given, position element randomly
  if (position.x === 0 && position.y === 0) {
    let boundingBox;
    if (data.type === 'state' || data.type === 'value') {
      boundingBox = parentBB;
    } else {
      boundingBox = cropViewport(cy.extent());
    }
    options.boundingBox = boundingBox;
    const layout = cy.layout(options);
    layout.run();
  }
  // 如果是状态/值节点且指定了位置，自动调整避免重叠
  else if ((data.type === 'state' || data.type === 'value') && data.parent) {
    const parent = cy.getElementById(data.parent);
    if (parent.length > 0) {
      const siblings = parent.children('[type="state"], [type="value"]');
      const siblingCount = siblings.length;

      if (siblingCount > 0) {
        // 获取父节点信息
        const parentBB = parent.boundingBox({ includeLabels: false });
        const padding = 10;
        const topMargin = 20; // 顶部留空，避免和文字区域重叠

        // 可用区域
        const availableLeft = parentBB.x1 + padding;
        const availableRight = parentBB.x2 - padding;
        const availableTop = parentBB.y1 + topMargin;
        const availableBottom = parentBB.y2 - padding;
        const availableWidth = availableRight - availableLeft;
        const availableHeight = availableBottom - availableTop;

        // 计算排列（优先横向）
        const columns = Math.ceil(Math.sqrt(siblingCount));
        const rows = Math.ceil(siblingCount / columns);

        const index = siblingCount - 1;
        const row = Math.floor(index / columns);
        const col = index % columns;

        // 计算网格中心位置
        const cellWidth = availableWidth / columns;
        const cellHeight = availableHeight / rows;

        const cellCenterX = availableLeft + cellWidth * (col + 0.5);
        const cellCenterY = availableTop + cellHeight * (row + 0.5);

        position.x = cellCenterX;
        position.y = cellCenterY;

        addedNode.position(position);
      }
    }
  }

  cy.elements().unlock();
};

/**
 * Adding edge to master model as well as in the current diagram
 * @param cy - Cytoscape instance
 * @param data - Edge data
 * @param shouldPropagate - Whether the edge should be propagated to other diagrams
 * @returns
 */
const addEdge = (
  cy: Core,
  data: EdgeData,
  shouldPropagate: boolean = false
): EdgeSingular | null => {
  const shouldDebugEdge = isParentChildInvocationEdgeData(data);
  if (cy.getElementById(data.id).length > 0) {
    return null;
  }
  if (data['MMRef'] === null) {
    let modelEdge = new MMEdge(
      data['id'],
      data['source'],
      data['target'],
      data['type'],
      shouldPropagate,
      undefined,
      data['label'] || '',
      data.linkQualifier ?? LinkQualifier.None,
      data.linkNegation ?? LinkNegation.None
    );
    originalEdgeArray.addEdge(modelEdge);
    data['MMRef'] = modelEdge;
  }

  const markerColor = data.markerColor || '#586D8C';
  const markerLabel = buildMarkerLabel(
    data.linkQualifier ?? LinkQualifier.None,
    data.linkNegation ?? LinkNegation.None
  );

  if (shouldDebugEdge) {
    opmcvEdgeDebugLog('addEdge:request', {
      id: data.id,
      type: data.type,
      label: data.label || '',
      sourceId: data.source?.id || null,
      targetId: data.target?.id || null,
      sourceParentId: (data.source?.parent as MMNode | null)?.id || null,
      targetParentId: (data.target?.parent as MMNode | null)?.id || null,
      shouldPropagate,
      edgeCountBeforeAdd: cy.edges().length,
    });
  }

  const addedEdge = cy.add({
    data: {
      ...data,
      source: data['source'].id,
      target: data['target'].id,
      markerLabel,
      markerColor,
    },
  });

  if (shouldDebugEdge) {
    const logEdgeState = (stage: string) => {
      const currentEdge = cy.getElementById(data.id);
      if (!currentEdge || currentEdge.empty()) {
        opmcvEdgeDebugLog(stage, {
          id: data.id,
          exists: false,
          edgeCount: cy.edges().length,
        });
        return;
      }
      const sameEndpointEdgeCount = cy
        .edges()
        .filter(
          (edge: any) =>
            edge.source().id() === data.source.id &&
            edge.target().id() === data.target.id &&
            edge.data('type') === data.type
        ).length;
      opmcvEdgeDebugLog(stage, {
        ...buildEdgeDebugSnapshot(currentEdge),
        edgeCount: cy.edges().length,
        sameEndpointEdgeCount,
      });
    };

    logEdgeState('addEdge:created');
    if (typeof requestAnimationFrame === 'function') {
      requestAnimationFrame(() => {
        logEdgeState('addEdge:raf1');
        requestAnimationFrame(() => {
          logEdgeState('addEdge:raf2');
        });
      });
    } else {
      setTimeout(() => {
        logEdgeState('addEdge:timeout');
      }, 0);
    }
  }

  refreshStructuralEdgeStyles(cy, addedEdge);
  return addedEdge;
};

const refreshStructuralEdgeStyles = (cy: Core, seedEdges?: any) => {
  if (!cy) {
    return;
  }

  const edgeIds = new Set<string>();

  const collectFromEdge = (edge: any) => {
    if (!edge || edge.empty?.()) {
      return;
    }

    if (typeof edge.id === 'function') {
      edgeIds.add(edge.id());
    }

    const source = edge.source?.();
    const target = edge.target?.();

    source?.connectedEdges?.().forEach((item: any) => {
      if (hierarchicalStructuralEdges.includes(item.data?.('type'))) {
        edgeIds.add(item.id());
      }
    });

    target?.connectedEdges?.().forEach((item: any) => {
      if (hierarchicalStructuralEdges.includes(item.data?.('type'))) {
        edgeIds.add(item.id());
      }
    });
  };

  if (Array.isArray(seedEdges)) {
    seedEdges.forEach(collectFromEdge);
  } else if (seedEdges && typeof seedEdges.forEach === 'function') {
    seedEdges.forEach((edge: any) => collectFromEdge(edge));
  } else if (seedEdges) {
    collectFromEdge(seedEdges);
  }

  if (edgeIds.size === 0) {
    cy.edges().forEach((edge: any) => {
      if (hierarchicalStructuralEdges.includes(edge.data('type'))) {
        edgeIds.add(edge.id());
      }
    });
  }

  const run = () => {
    if (!cy || edgeIds.size === 0) {
      return;
    }

    const edgesToUpdate = cy
      .edges()
      .filter((edge: any) => edgeIds.has(edge.id()));

    if (edgesToUpdate.length === 0) {
      return;
    }

    edgesToUpdate.forEach((edge: any) => {
      edge.updateStyle();
    });
  };

  run();

  if (typeof requestAnimationFrame === 'function') {
    requestAnimationFrame(() => {
      run();
      requestAnimationFrame(() => {
        run();
      });
    });
  }
};

/**
 * Actions to be done on removal of a node from context menu.
 * @param node - Cytoscape node
 * @param dispatch - React reducer function to update UI (when main node of a diagram is deleted,
 *    it has to be removed from the diagram tree)
 */
const removeNodeContextMenu = (
  node: NodeSingular,
  dispatch: Function,
  currentDiagram?: DiagramTreeNode
) => {
  const MMRef = node.data('MMRef') as MMNode;

  // 跳过没有 MMRef 的节点（如复制粘贴的节点）
  if (!MMRef) {
    node.data({ MMRef: null });
    return;
  }

  const isStateOrValueNode =
    MMRef.type === NodeType.State || MMRef.type === NodeType.Value;
  const parentNode = node.parent();
  const parentMMRef = parentNode?.length
    ? (parentNode.data('MMRef') as MMNode)
    : null;

  if (MMRef.diagram !== null) {
    const diagram = MMRef.diagram;
    detachDiagramSubtree(diagram);
    dispatch({ type: ACTIONS.UPDATE_TREE });
  }

  MMRef.deleted = true;
  node.data({ MMRef: null });

  for (const childNode of MMRef.children || []) {
    if (childNode.type === 'state') {
      childNode.deleted = true;
    }
  }

  //mark connected edges as removed
  for (const connectedEdge of node.connectedEdges().toArray()) {
    const edgeMMRef = connectedEdge.data('MMRef');

    // 跳过没有 MMRef 的边
    if (!edgeMMRef) {
      connectedEdge.data({ MMRef: null });
      continue;
    }

    MMRef.deleted = true;
    originalEdgeArray.removeEdge(edgeMMRef);
    if (edgeMMRef.originalEdges?.length) {
      originalEdgeArray.removeOriginalEdges(edgeMMRef.originalEdges);
    } else {
      originalEdgeArray.removeEdge(edgeMMRef);
      edgeMMRef.removeAllDerived?.();
    }
    connectedEdge.data({ MMRef: null });
  }

  if (isStateOrValueNode && parentMMRef && parentNode?.length) {
    const hasRemainingStateChildren = (parentMMRef.children || []).some(
      child => child.type === NodeType.State && !child.deleted
    );
    const hasRemainingValueChildren = (parentMMRef.children || []).some(
      child => child.type === NodeType.Value && !child.deleted
    );
    const parentNodeSingular = parentNode.first();

    if (hasRemainingStateChildren) {
      parentMMRef.childGroupType = ChildGroupType.State;
      parentNodeSingular.data('childGroupType', ChildGroupType.State);
    } else if (hasRemainingValueChildren) {
      parentMMRef.childGroupType = ChildGroupType.Value;
      parentNodeSingular.data('childGroupType', ChildGroupType.Value);
    } else {
      parentMMRef.childGroupType = ChildGroupType.None;
      parentNodeSingular.data('childGroupType', ChildGroupType.None);
    }

    const parentLabel = parentMMRef.label;
    const newDisplayLabel = computeDisplayLabel(parentLabel, parentMMRef);
    parentNodeSingular.data('displayLabel', newDisplayLabel);
    parentNodeSingular.style('label', newDisplayLabel);
    applyAutoLabelWidth(parentNodeSingular, newDisplayLabel);

    // state/value 删除需要同步到其他图层中同父节点实例（仅同步节点信息，不做连线级联）。
    syncStateValueNodeRemovalFromDiagrams(String(MMRef.id), parentMMRef);
    relayoutStateValueChildren(parentNodeSingular, node.id());
    if (currentDiagram) {
      syncStateValueLayoutToDiagrams(
        parentNodeSingular,
        parentMMRef,
        currentDiagram,
        node.id()
      );
    }
  }
};

/**
 * Actions to be done on removal of an edge from context menu.
 * @param edge - Cytoscape edge
 */
const removeEdgeContextMenu = (edge: EdgeSingular) => {
  const MMRef = edge.data('MMRef') as MMEdge;

  // 跳过没有 MMRef 的边（如复制粘贴的边）
  if (!MMRef) {
    edge.data({ MMRef: null });
    return;
  }

  if (MMRef.originalEdges?.length) {
    originalEdgeArray.removeOriginalEdges(MMRef.originalEdges);
    for (const edge of MMRef.originalEdges) {
      edge.removeAllDerived?.();
    }
  } else {
    originalEdgeArray.removeEdge(MMRef);
    MMRef.removeAllDerived?.();
  }

  edge.data({ MMRef: null });
};

/**
 * Actions to be done on addition of an element from context menu.
 * @param cy - Cytoscape instance
 * @param event - Event object
 * @param type - Type of removed object
 * @param currentDiagram - Model node of current diagram
 */
const addNodeFromContextMenu = (
  cy: Core,
  event: any,
  type: NodeType,
  currentDiagram: DiagramTreeNode
) => {
  currentMMNode = resolveDiagramTopLevelParent(currentDiagram.mainNode);
  const counter = eleCounter.value;
  const isObjectOrProcess =
    type === NodeType.Object || type === NodeType.Process;
  const isStateOrValue = type === NodeType.State || type === NodeType.Value;
  const baseLabel =
    type === NodeType.Object
      ? 'Object'
      : type === NodeType.Process
        ? 'Process'
        : type === NodeType.State
          ? 'State'
          : 'Value';
  const uniqueLabel = isObjectOrProcess
    ? generateUniqueSequencedValue(
        baseLabel,
        buildUsedNodeLabelSet(),
        baseLabel
      )
    : baseLabel;
  const usedCodeNames = isObjectOrProcess ? buildUsedNodeCodeNameSet() : null;
  const uniqueCodeName = isObjectOrProcess
    ? generateUniqueCodeNameByLabel(uniqueLabel, usedCodeNames as Set<string>)
    : '';
  let data = {
    id: counter,
    label: uniqueLabel,
    type: type,
    parent: '',
    MMRef: null,
  };

  let pos = event.position;
  let nodePosition = {
    x: pos.x,
    y: pos.y,
  };
  if (event.target !== cy) {
    //on element
    data['parent'] = event.target.id() as string;
    const parentMMRef = event.target.data('MMRef') as MMNode;
    const parentNode = event.target;

    // 根据添加的子节点类型，更新父节点的 childGroupType
    const isStateType = (type as string) === 'state';
    const isValueType = (type as string) === 'value';

    if (parentMMRef) {
      if (isStateType) {
        // 添加状态节点：设置为 State 类型
        parentMMRef.childGroupType = ChildGroupType.State;
        parentNode.data('childGroupType', ChildGroupType.State);
      } else if (isValueType) {
        // 添加值节点：设置为 Value 类型
        parentMMRef.childGroupType = ChildGroupType.Value;
        parentNode.data('childGroupType', ChildGroupType.Value);
      }
    }

    // state/value 节点按“父节点范围”做自动编号命名，不扩散到全图或跨图层。
    if (isStateOrValue && parentMMRef) {
      data['label'] = generateUniqueSequencedValue(
        baseLabel,
        buildUsedStateValueLabelSetByParent(parentMMRef),
        baseLabel
      );
    }

    addNode(cy, data, nodePosition, parentMMRef);
    const createdNode = cy.getElementById(counter);

    if (isStateOrValue && parentNode.length > 0) {
      relayoutStateValueChildren(parentNode);
    }

    // state/value 新增需要同步到其他图层中“同父节点”的实例。
    if (isStateOrValue && createdNode.length > 0 && parentMMRef) {
      const createdNodeSingular = createdNode[0] as NodeSingular;
      if (createdNodeSingular) {
        syncStateValueNodeAdditionToDiagrams(
          createdNodeSingular,
          parentMMRef,
          currentDiagram
        );
      }
    }

    if (isObjectOrProcess) {
      if (createdNode.length > 0) {
        const createdMMRef = createdNode.data('MMRef') as MMNode | null;
        if (createdMMRef) {
          createdMMRef.codeName = uniqueCodeName;
          createdNode.data('codeName', uniqueCodeName);
        }
      }
    }

    // 如果是值节点，更新父节点的 displayLabel（添加 [] 后缀）
    if (isValueType && parentMMRef) {
      const parentLabel = parentMMRef.label;
      const parentDisplayLabel = computeDisplayLabel(parentLabel, parentMMRef);
      parentNode.data('displayLabel', parentDisplayLabel);
      parentNode.style('label', parentDisplayLabel);
      applyAutoLabelWidth(parentNode, parentDisplayLabel);
    }
  } else {
    addNode(cy, data, nodePosition, currentMMNode);
    if (isObjectOrProcess) {
      const createdNode = cy.getElementById(counter);
      if (createdNode.length > 0) {
        const createdMMRef = createdNode.data('MMRef') as MMNode | null;
        if (createdMMRef) {
          createdMMRef.codeName = uniqueCodeName;
          createdNode.data('codeName', uniqueCodeName);
        }
      }
    }
  }
};

/**
 * Copying of in-zoomed node and addition of placeholder subnodes
 * 支持过程节点和对象节点的 in-zoom：
 * - 过程节点：创建两个子过程
 * - 对象节点：创建两个子对象
 * @param cy - Cytoscape instance
 * @param event - Event object
 */
const addInzoomedNodes = (cy: Core, event: any) => {
  let inzoomedNode = event.target;
  let type = inzoomedNode.data('type') as NodeType;
  let parentId = inzoomedNode.id() as string;

  const viewport = cy.extent();
  const centerX = (viewport.x1 + viewport.x2) / 2;
  const centerY = (viewport.y1 + viewport.y2) / 2;

  // add inzoomed node to new diagram
  addNode(cy, inzoomedNode.data(), { x: centerX, y: centerY }, currentMMNode);

  let MMRef = inzoomedNode.data('MMRef') as MMNode;

  // 设置父节点的 childGroupType 为 InZoom
  if (MMRef) {
    MMRef.childGroupType = ChildGroupType.InZoom;
  }
  // 同步到新图中的 cytoscape 节点
  const cyParentNode = cy.getElementById(parentId);
  if (cyParentNode.length > 0) {
    cyParentNode.data('childGroupType', ChildGroupType.InZoom);
  }

  // 根据节点类型决定子节点类型
  // 过程节点创建子过程，对象节点创建子对象
  const childType =
    type === NodeType.Process ? NodeType.Process : NodeType.Object;

  const baseLabel = childType === NodeType.Process ? 'Process' : 'Object';
  const usedLabels = buildUsedNodeLabelSet();
  const usedCodeNames = buildUsedNodeCodeNameSet();

  const addDefaultChild = (position: { x: number; y: number }) => {
    const counter = eleCounter.value;
    const uniqueLabel = generateUniqueSequencedValue(
      baseLabel,
      usedLabels,
      baseLabel
    );
    usedLabels.add(uniqueLabel);
    const uniqueCodeName = generateUniqueCodeNameByLabel(
      uniqueLabel,
      usedCodeNames
    );
    usedCodeNames.add(uniqueCodeName);

    const data = {
      id: counter,
      label: uniqueLabel,
      parent: parentId,
      type: childType,
      MMRef: null,
    };
    addNode(cy, data, position, MMRef);

    const createdNode = cy.getElementById(counter);
    if (createdNode.length > 0) {
      const createdMMRef = createdNode.data('MMRef') as MMNode | null;
      if (createdMMRef) {
        createdMMRef.codeName = uniqueCodeName;
        createdNode.data('codeName', uniqueCodeName);
      }
    }
  };

  // add 2 default subnodes
  addDefaultChild({ x: centerX - 20, y: centerY - 40 });
  addDefaultChild({ x: centerX + 20, y: centerY + 40 });
};

/**
 * Get connected edges to a given master model node
 * @param MMNode - Master model node
 * @param array - Original edge array or derived edge array
 * @returns - Array of master model edges connected to the given node
 */
const getConnectedEdges = (MMNode: MMNode, array: EdgeArray): Array<MMEdge> => {
  let connectedEdges = array.findIngoingEdges(MMNode);
  connectedEdges = connectedEdges.concat(array.findOutgoingEdges(MMNode));
  return connectedEdges;
};

/**
 * Determine wheter given element is present and visible in the current diagram.
 * @param cy - Cytoscape instance
 * @param id - element id
 * @returns - True if exists on current diagram and is not hidden, else false
 */
const eleAlreadyInAndVisible = (cy: Core, id: string): boolean => {
  const ele = cy.getElementById(id);
  const result = ele.length > 0 && ele.data('display') !== 'none';
  return result;
};

/**
 * Create node data for the cytoscape node
 * @param node - Master model node
 * @returns: Data object
 */
const createCytoNodeData = (node: MMNode): any => {
  return {
    id: node.id,
    MMRef: node,
    label: node.label,
    type: node.type,
    essence: node.essence,
    affiliation: node.affiliation,
  };
};

/**
 * Create edge data for the cytoscape edge
 * @param edge - Master model node
 * @returns: Data object
 */
const createCytoEdgeData = (edge: MMEdge): any => {
  let label = edge.label;
  if (edge.originalEdges.length) label = edge.originalEdges[0].label;

  const markerLabel = buildMarkerLabel(edge.linkQualifier, edge.linkNegation);
  const markerColor = edge.originalEdges.length ? '#ccc' : '#586D8C';

  return {
    id: edge.id,
    source: edge.source,
    target: edge.target,
    MMRef: edge,
    label: label,
    type: edge.type,
    linkQualifier: edge.linkQualifier,
    linkNegation: edge.linkNegation,
    markerLabel,
    markerColor,
  };
};

/**
 * Determine if the given derived edge should be skipped (because a more specific one could be added instead)
 * @param cy - Cytoscape instance
 * @param edge - Master model edge
 * @returns - True if should be skipped, false if not
 */
const skipDerivedEdge = (cy: Core, edge: MMEdge): boolean => {
  if (edge.preferedEdge !== null) {
    const sourceId = edge.preferedEdge.source.id;
    const targetId = edge.preferedEdge.target.id;
    if (
      eleAlreadyInAndVisible(cy, sourceId) &&
      eleAlreadyInAndVisible(cy, targetId)
    )
      return true;
  }
  //@ts-ignore
  for (const diagramEdge of cy.edges()) {
    const originalEdges = diagramEdge.data('MMRef').originalEdges;
    if (originalEdges.length > 1) {
      for (const origEdge of originalEdges) {
        if (eleAlreadyInAndVisible(cy, origEdge.id)) return true;
      }
    }
  }
  return false;
};

const derivedAlreadyIn = (cy: Core, edge: MMEdge): boolean => {
  for (const derived of edge.derivedEdges) {
    if (cy.getElementById(derived.id).data('MMRef') === derived) {
      return true;
    }
  }
  // @ts-ignore
  for (const diagramEdge of cy.edges()) {
    if (diagramEdge.data('MMRef').preferedEdge == edge) return true;
  }
  return false;
};

/**
 * Add an array of edges to given node
 * @param cy - Cytoscape instance
 * @param MMNode - Given master model node
 * @param edges - Array of master model edges
 */
const addEdgeArray = (cy: Core, MMNode: MMNode, edges: Array<MMEdge>) => {
  for (const edge of edges) {
    if (
      eleAlreadyInAndVisible(cy, edge.id) ||
      edge.source.deleted ||
      edge.target.deleted ||
      edge.propagation == false
    ) {
      continue;
    }

    if (edge.originalEdges.length > 0 && skipDerivedEdge(cy, edge)) continue;

    if (derivedAlreadyIn(cy, edge)) continue;

    let connectedNode;
    if (MMNode === edge.source) connectedNode = edge.target;
    else connectedNode = edge.source;

    if (!eleAlreadyInAndVisible(cy, connectedNode.id)) {
      let parent = connectedNode.parent as MMNode;
      const nodeData = createCytoNodeData(connectedNode);

      if (connectedNode.type === 'state') {
        nodeData['parent'] = parent ? parent.id : null;
        if (!eleAlreadyInAndVisible(cy, parent.id)) {
          const nodeParentData = createCytoNodeData(parent);
          addNode(cy, nodeParentData, { x: 0, y: 0 }, currentMMNode);
        }
      }
      addNode(cy, nodeData, { x: 0, y: 0 }, parent);
    }

    const edgeData = createCytoEdgeData(edge);
    addEdge(cy, edgeData);
  }
};

/**
 * Add connected edge after selecting it from the Bring Connected selection
 * @param cy - Cytoscape instance
 * @param edge - Selected edge
 * @param targetNode - Node that the Bring Connected was invoked on
 */
const bringConnectedEdge = (cy: Core, edge: MMEdge, targetNode: MMNode) => {
  let connectedNode;
  if (targetNode === edge.source) connectedNode = edge.target;
  else connectedNode = edge.source;

  const diagramEdge = cy.getElementById(edge.id);
  if (diagramEdge.length > 0 && diagramEdge.data('display') == 'none') {
    diagramEdge.remove();
  }
  const cyConnectedNode = cy.getElementById(connectedNode.id);
  cyConnectedNode.data({ display: 'element' });
  if (connectedNode.type === 'state') {
    //@ts-ignore
    const parent = connectedNode.parent as MMNode;
    cyConnectedNode.move({ parent: parent.id });
    const cyParentNode = cy.getElementById(parent.id);
    cyParentNode.data({ display: 'element' });
  }

  cy.getElementById(edge.id).data({ display: 'element' });

  if (!eleAlreadyInAndVisible(cy, connectedNode.id)) {
    let parent = connectedNode.parent as MMNode;
    const nodeData = createCytoNodeData(connectedNode);

    if (connectedNode.type === 'state') {
      nodeData['parent'] = parent ? parent.id : null;
      if (!eleAlreadyInAndVisible(cy, parent.id)) {
        const nodeParentData = createCytoNodeData(parent);
        addNode(cy, nodeParentData, { x: 0, y: 0 }, currentMMNode);
      }
    }
    addNode(cy, nodeData, { x: 0, y: 0 }, parent);
  }

  const edgeData = createCytoEdgeData(edge);
  addEdge(cy, edgeData);
};

/**
 * Find connected edges from the master model and connect them.
 * @param cy - Cytoscape instance
 * @param MMNode - Master model node to which elements are connected to
 */
const addConnectedNodes = (cy: Core, MMNode: MMNode) => {
  let connectedOriginalEdges = getConnectedEdges(MMNode, originalEdgeArray);
  let connectedStructuralEdges = connectedOriginalEdges.filter(edge =>
    hierarchicalStructuralEdges.includes(edge.type)
  );
  let connectedDerivedEdges = getConnectedEdges(MMNode, derivedEdgeArray);
  addEdgeArray(cy, MMNode, connectedStructuralEdges);
  addEdgeArray(cy, MMNode, connectedDerivedEdges);
  addEdgeArray(cy, MMNode, connectedOriginalEdges);
};

/**
 * Adding connected nodes to just inzoomed node, original edges are prefered.
 * @param cy - Cytoscape instance
 * @param MMNode - In-zoomed master model node
 */
const addConnectedNodesInzoom = (cy: Core, MMNode: MMNode) => {
  let connectedOriginalEdges = getConnectedEdges(MMNode, originalEdgeArray);
  let connectedDerivedEdges = getConnectedEdges(MMNode, derivedEdgeArray);
  addEdgeArray(cy, MMNode, connectedOriginalEdges);
  addEdgeArray(cy, MMNode, connectedDerivedEdges);
};

const getAllConnectedEdges = (cy: Core, node: MMNode): Array<MMEdge> => {
  const connectedOriginalEdges = getConnectedEdges(node, originalEdgeArray);
  const connectedDerivedEdges = getConnectedEdges(node, derivedEdgeArray);
  const allConnected = connectedOriginalEdges.concat(connectedDerivedEdges);
  const filteredConnected = allConnected.filter(edge => {
    let connectedNode;
    if (node === edge.source) connectedNode = edge.target;
    else connectedNode = edge.source;

    // 容错：跳过 source 或 target 为 null 的无效边
    if (!connectedNode) {
      return false;
    }

    let result = false;
    if (
      !eleAlreadyInAndVisible(cy, edge.id) ||
      !eleAlreadyInAndVisible(cy, connectedNode.id)
    )
      result = true;
    if (
      connectedNode.isSubelementOfMain &&
      !eleAlreadyInAndVisible(cy, connectedNode.id)
    )
      result = false;
    return result;
  });

  return filteredConnected;
};
/**
 * Find all states of the given node and add them into the current diagram
 * @param cy - Cytoscape instance
 * @param node - Cytoscape node
 */
const bringAllStates = (cy: Core, node: NodeSingular) => {
  const MMRef = node.data('MMRef');
  for (const child of MMRef.children) {
    if (child.type === 'state') {
      const data = {
        id: child.id,
        label: child.label,
        type: NodeType.State,
        MMRef: child,
        parent: MMRef.id,
      };
      let cyNode = cy.getElementById(child.id);
      if (cyNode.length > 0) {
        cyNode.move({ parent: MMRef.id }); //move out of the compound node so when no children are displayed, the parent node is no longer compound
        cyNode.data({ display: 'element' });
      } else {
        addNode(cy, data, { x: 0, y: 0 }, MMRef);
      }
    }
  }
};

export {
  addNode,
  addNodeFromContextMenu,
  addInzoomedNodes,
  generateUniqueSequencedValue,
  buildUsedNodeLabelSet,
  buildUsedNodeCodeNameSet,
  generateUniqueCodeNameByLabel,
  addConnectedNodesInzoom,
  addConnectedNodes,
  addEdge,
  refreshStructuralEdgeStyles,
  removeNodeContextMenu,
  removeEdgeContextMenu,
  eleAlreadyInAndVisible,
  getAllConnectedEdges,
  bringAllStates,
  createCytoEdgeData,
  bringConnectedEdge,
  detachDiagramSubtree,
  resolveDiagramTopLevelParent,
  clampChildNodeWithinParentTitleSafeArea,
  clampCompoundChildrenAwayFromTitle,
  relayoutStateValueChildren,
};
